import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { siteFirewall } from "../../api/endpoints/siteFirewall";
import { qk } from "../../api/queryKeys";
import { ApiError } from "../../api/errors";
import type { FirewallPolicy, FirewallPolicyIn } from "../../api/types/siteFirewall";
import type { NetworkDefaultsOut } from "../../api/types/networkDefaults";
import { FormShell } from "../../components/forms/FormShell";
import { SelectField } from "../../components/forms/SelectField";
import { CheckboxField } from "../../components/forms/CheckboxField";
import { ErrorBanner } from "../../components/ui/ErrorBanner";
import { EmptyState } from "../../components/ui/EmptyState";
import { useSiteVlanNames } from "./useSiteVlanNames";
import { useNetworkDefaults } from "./useNetworkDefaults";
import { GeneratedFromAllocationBanner } from "./GeneratedFromAllocationBanner";

type CreateMode = "idle" | "manual" | "prefill";

const FORWARD_OPTIONS = [
  { value: "deny", label: "Deny" },
  { value: "allow", label: "Allow" },
] as const;

const POLICY_DEFAULTS: FirewallPolicyIn = {
  allow_established_related: true,
  inter_vlan_default_forward: "deny",
  outbound_default_action: "deny",
  outbound_deny_vlans: [],
};

export function FirewallPolicyEditor({
  accountSlug,
  siteSlug,
  canWrite,
}: {
  accountSlug: string;
  siteSlug: string;
  canWrite: boolean;
}) {
  const [mode, setMode] = useState<CreateMode>("idle");
  const [prefillAttempt, setPrefillAttempt] = useState(0);
  const startPrefill = () => {
    setPrefillAttempt((n) => n + 1); // fresh fetch even on retry / re-entry
    setMode("prefill");
  };
  const q = useQuery({
    retry: false, // 404 = no policy yet, not an error to retry
    queryKey: qk.siteFirewallPolicy(siteSlug),
    queryFn: () => siteFirewall.getPolicy(siteSlug).then((r) => r.data),
  });
  const defaults = useNetworkDefaults(siteSlug, {
    enabled: mode === "prefill",
    attempt: prefillAttempt,
  });

  if (q.isPending) {
    return <p style={{ color: "var(--text-dim)" }}>Loading firewall policy…</p>;
  }
  const is404 = q.error instanceof ApiError && q.error.status === 404;
  if (q.error && !is404) {
    return <ErrorBanner error={q.error} title="Failed to load firewall policy" />;
  }

  if (q.data) {
    return (
      <PolicyForm
        key={`existing-${siteSlug}`}
        accountSlug={accountSlug}
        siteSlug={siteSlug}
        canWrite={canWrite}
        initial={toIn(q.data)}
        existing
      />
    );
  }
  if (mode === "manual") {
    return (
      <PolicyForm
        key={`new-${siteSlug}`}
        accountSlug={accountSlug}
        siteSlug={siteSlug}
        canWrite={canWrite}
        initial={POLICY_DEFAULTS}
        onCancel={() => setMode("idle")}
      />
    );
  }
  if (mode === "prefill") {
    if (defaults.isPending) {
      return (
        <p style={{ color: "var(--text-dim)" }}>
          Computing firewall defaults from this site's allocation…
        </p>
      );
    }
    if (defaults.error) {
      return (
        <div style={{ display: "grid", gap: 12 }}>
          <ErrorBanner error={defaults.error} title="Can't prefill from allocation yet" />
          <PolicyPrefillActions canWrite={canWrite} onPrefill={startPrefill} onManual={() => setMode("manual")} />
        </div>
      );
    }
    if (defaults.data) {
      return (
        <PolicyForm
          key={`prefill-${siteSlug}-${prefillAttempt}`}
          accountSlug={accountSlug}
          siteSlug={siteSlug}
          canWrite={canWrite}
          initial={defaults.data.firewall_policy}
          source={defaults.data.source}
          onCancel={() => setMode("idle")}
        />
      );
    }
  }
  return (
    <EmptyState
      title="No firewall policy"
      description="This site has no firewall policy yet."
      action={
        canWrite ? (
          <PolicyPrefillActions canWrite={canWrite} onPrefill={startPrefill} onManual={() => setMode("manual")} />
        ) : null
      }
    />
  );
}

function PolicyPrefillActions({
  canWrite,
  onPrefill,
  onManual,
}: {
  canWrite: boolean;
  onPrefill: () => void;
  onManual: () => void;
}) {
  if (!canWrite) return null;
  return (
    <span style={{ display: "inline-flex", gap: 8 }}>
      <button type="button" style={primaryBtn} onClick={onPrefill}>
        Prefill from allocation
      </button>
      <button type="button" style={secondaryBtn} onClick={onManual}>
        Create policy
      </button>
    </span>
  );
}

// ---------------------------------------------------------------------------

function PolicyForm({
  accountSlug,
  siteSlug,
  canWrite,
  initial,
  existing,
  onCancel,
  source,
}: {
  accountSlug: string;
  siteSlug: string;
  canWrite: boolean;
  initial: FirewallPolicyIn;
  existing?: boolean;
  onCancel?: () => void;
  source?: NetworkDefaultsOut["source"];
}) {
  const qc = useQueryClient();
  const vlan = useSiteVlanNames(accountSlug, siteSlug);
  const [form, setForm] = useState<FirewallPolicyIn>(() => ({
    ...initial,
    outbound_deny_vlans: initial.outbound_deny_vlans ?? [],
  }));
  const set = <K extends keyof FirewallPolicyIn>(k: K, v: FirewallPolicyIn[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const save = useMutation({
    mutationFn: (body: FirewallPolicyIn) => siteFirewall.putPolicy(siteSlug, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.siteFirewallPolicy(siteSlug) });
      onCancel?.();
    },
  });

  const denySelected = form.outbound_deny_vlans ?? [];
  // Freeze all write controls for read-only users AND while a save is in flight
  // (so the displayed form can't diverge from the submitted body).
  const disabled = !canWrite || save.isPending;

  return (
    <FormShell
      submitLabel={existing ? "Save policy" : "Create policy"}
      onSubmit={(e) => {
        e.preventDefault();
        if (disabled) return;
        save.mutate(form);
      }}
      onCancel={onCancel}
      pending={save.isPending}
      submitDisabled={!canWrite}
      error={save.error}
      errorTitle="Firewall policy save failed"
    >
      {source && <GeneratedFromAllocationBanner source={source} />}
      <CheckboxField
        label="Allow established/related"
        checked={form.allow_established_related}
        onChange={(v) => set("allow_established_related", v)}
        helper="Accept return traffic for established connections."
        disabled={disabled}
      />
      <SelectField
        label="Inter-VLAN default forward"
        value={form.inter_vlan_default_forward}
        onChange={(v) => set("inter_vlan_default_forward", v as "deny" | "allow")}
        options={FORWARD_OPTIONS}
        helper="Default action for traffic between VLANs (deny = isolated by default)."
        disabled={disabled}
      />
      <SelectField
        label="Outbound default action"
        value={form.outbound_default_action}
        onChange={(v) => set("outbound_default_action", v as "deny" | "allow")}
        options={FORWARD_OPTIONS}
        helper="Default action for VLAN → WAN (internet) traffic."
        disabled={disabled}
      />
      <VlanDenyMultiSelect
        available={vlan.names}
        selected={denySelected}
        onChange={(v) => set("outbound_deny_vlans", v)}
        disabled={disabled}
        pending={vlan.isPending}
        loadError={vlan.error}
      />
    </FormShell>
  );
}

// Closed-list multiselect over the site's GENERATED VLAN names. Renders the
// union of available names + any already-selected name that's no longer in the
// template (so a stale selection is visible + removable, not hidden).
function VlanDenyMultiSelect({
  available,
  selected,
  onChange,
  disabled,
  pending,
  loadError,
}: {
  available: string[];
  selected: string[];
  onChange: (v: string[]) => void;
  disabled: boolean;
  pending: boolean;
  loadError: unknown;
}) {
  const all = [...available];
  for (const s of selected) if (!all.includes(s)) all.push(s);
  const toggle = (name: string) =>
    onChange(
      selected.includes(name)
        ? selected.filter((n) => n !== name)
        : [...selected, name],
    );

  return (
    <div style={{ display: "grid", gap: 6 }}>
      <span style={{ fontSize: "0.8rem", color: "var(--text-dim)" }}>
        Outbound deny VLANs
      </span>
      {pending ? (
        <span style={{ fontSize: "0.85rem", color: "var(--text-dim)" }}>Loading VLANs…</span>
      ) : all.length === 0 ? (
        <span style={{ fontSize: "0.85rem", color: "var(--text-dim)" }}>
          No generated VLANs yet — allocate the site's VLANs first.
        </span>
      ) : (
        <div style={{ display: "grid", gap: 4 }}>
          {all.map((name) => (
            <label
              key={name}
              style={{ display: "flex", alignItems: "center", gap: 8, fontSize: "0.85rem" }}
            >
              <input
                type="checkbox"
                checked={selected.includes(name)}
                disabled={disabled}
                onChange={() => toggle(name)}
              />
              {name}
              {!available.includes(name) && (
                <span style={{ color: "var(--warn)", fontSize: "0.75rem" }}>
                  (not in current template)
                </span>
              )}
            </label>
          ))}
        </div>
      )}
      {loadError ? (
        <span style={{ fontSize: "0.75rem", color: "var(--crit)" }}>
          Couldn't load VLAN names; selection limited to already-saved values.
        </span>
      ) : (
        <span style={{ fontSize: "0.75rem", color: "var(--text-dim)" }}>
          VLANs blocked from outbound internet. Validated server-side against the
          site's generated VLANs.
        </span>
      )}
    </div>
  );
}

function toIn(p: FirewallPolicy): FirewallPolicyIn {
  return {
    allow_established_related: p.allow_established_related,
    inter_vlan_default_forward: p.inter_vlan_default_forward,
    outbound_default_action: p.outbound_default_action,
    outbound_deny_vlans: p.outbound_deny_vlans ?? [],
  };
}

const primaryBtn: React.CSSProperties = {
  background: "var(--accent)",
  color: "var(--bg)",
  border: "none",
  padding: "8px 16px",
  borderRadius: 4,
  fontSize: "0.9rem",
  fontWeight: 600,
  cursor: "pointer",
};

const secondaryBtn: React.CSSProperties = {
  background: "transparent",
  color: "var(--text-dim)",
  border: "1px solid var(--border)",
  padding: "8px 16px",
  borderRadius: 4,
  fontSize: "0.9rem",
  cursor: "pointer",
};
